// ── KPI Definitions Registry ──────────────────────────────────
// Static metadata for every KPI tracked across tools.
// Provides human-readable definitions, dynamic cause text, and remediation guidance.

export interface KpiDefinition {
  id: string;
  tool: string;
  label: string;
  definition: string;
  cause: string;
  remediation: string[];
  itemsUnavailableHint?: string;
}

type CauseTemplateFn = (value: unknown) => string;
type RemediationTemplateFn = (value: unknown) => string[];

interface KpiSpec {
  id: string;
  tool: string;
  label: string;
  definition: string;
  causeTemplate: CauseTemplateFn;
  remediationTemplate: RemediationTemplateFn;
  itemsUnavailableHint?: (value: unknown) => string | undefined;
}

// ── Helper ──────────────────────────────────────────────────────
function numVal(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return parseFloat(v) || 0;
  return 0;
}

function boolVal(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 100 || v === '100') return true;
  return false;
}

// ── KPI Specs ───────────────────────────────────────────────────
const KPI_SPECS: KpiSpec[] = [
  // ── SonarQube ──
  {
    id: 'sonar_gate_passed',
    tool: 'SonarQube',
    label: 'Quality Gate',
    definition: 'Whether the project passes SonarQube\'s quality gate — a configurable set of metric thresholds that must all be met.',
    causeTemplate: (v) => boolVal(v)
      ? 'Quality gate is passing — all configured metric thresholds are met.'
      : 'The quality gate is failing — one or more SonarQube metric thresholds (e.g. coverage, duplications, new issues) are not met.',
    remediationTemplate: (v) => boolVal(v) ? [] : ['Review the failing conditions in SonarQube\'s quality gate and fix the metrics that are below threshold.'],
  },
  {
    id: 'sonar_vulns',
    tool: 'SonarQube',
    label: 'Vulnerability Count',
    definition: 'Total critical and high severity vulnerabilities found in source code by static analysis.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `SonarQube's static analysis found ${n} critical/high severity vulnerabilit${n === 1 ? 'y' : 'ies'} in this repository's source code — e.g. injection flaws, insecure crypto, or unsafe deserialization patterns.`
        : 'No critical or high severity vulnerabilities were flagged by SonarQube for this repository.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Fix ${n} critical/high severity vulnerabilit${n === 1 ? 'y' : 'ies'} flagged by SonarQube static analysis.`] : [];
    },
  },
  {
    id: 'sonar_hotspots',
    tool: 'SonarQube',
    label: 'Security Hotspots',
    definition: 'Code locations that need manual security review — potential vulnerabilities requiring human judgment to confirm/dismiss.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} code location${n === 1 ? '' : 's'} touch sensitive operations (e.g. auth, crypto, SQL, file I/O) and were flagged as needing a manual review.`
        : 'No unreviewed security hotspots for this repository.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Review and triage ${n} security hotspot${n === 1 ? '' : 's'} in SonarQube (mark as reviewed or fixed).`] : [];
    },
    itemsUnavailableHint: (v) => numVal(v) > 0
      ? 'SonarQube only reports the hotspot count here — open the Security Hotspots tab in SonarQube to see and triage each one individually.'
      : undefined,
  },
  {
    id: 'sonar_code_smells',
    tool: 'SonarQube',
    label: 'Code Smells',
    definition: 'Maintainability issues in the code — patterns that are not bugs but make the code harder to understand, maintain, or evolve.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} code smell${n === 1 ? '' : 's'} detected — these are maintainability issues such as overly complex methods, duplicated logic, or naming violations.`
        : 'No code smells detected.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Address ${n} code smell${n === 1 ? '' : 's'} to improve maintainability. Focus on the highest-effort smells first — use SonarQube's Issues view filtered to Maintainability.`] : [];
    },
  },
  {
    id: 'sonar_duplications',
    tool: 'SonarQube',
    label: 'Code Duplications',
    definition: 'Percentage of duplicated lines across all files, as measured by SonarQube. High duplication increases maintenance burden and the risk of divergent fixes.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 3
        ? `${n.toFixed(1)}% of lines are duplicated — above the recommended 3% threshold. This commonly happens when logic is copy-pasted rather than extracted into shared utilities or services.`
        : `${n.toFixed(1)}% code duplication — within acceptable limits.`;
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 3
        ? [
            `Refactor the ${n.toFixed(1)}% duplicated code into shared utilities or abstractions.`,
            'Use SonarQube\'s Duplications tab to locate the exact duplicated blocks.',
          ]
        : [];
    },
  },
  {
    id: 'sonar_debt',
    tool: 'SonarQube',
    label: 'Technical Debt',
    definition: 'Estimated effort (in minutes/hours/days) required to fix all maintainability issues in the codebase, as computed by SonarQube.',
    causeTemplate: (v) => {
      const mins = numVal(v);
      const display = mins >= 480 ? `${Math.round(mins / 480)}d` : mins >= 60 ? `${Math.round(mins / 60)}h` : `${mins}min`;
      return mins > 480
        ? `Technical debt is ${display} — a significant investment to fully remediate. This accumulates when quick fixes, code smells, and shortcuts are not addressed promptly.`
        : `Technical debt is ${display} — manageable with regular refactoring sprints.`;
    },
    remediationTemplate: (v) => {
      const mins = numVal(v);
      const display = mins >= 480 ? `${Math.round(mins / 480)}d` : mins >= 60 ? `${Math.round(mins / 60)}h` : `${mins}min`;
      return mins > 0
        ? [
            `Allocate capacity to reduce ${display} of technical debt — prioritise issues flagged as Critical or High severity in SonarQube.`,
            'Set up a recurring "debt sprint" to steadily reduce the backlog.',
          ]
        : [];
    },
  },

  {
    id: 'sonar_coverage',
    tool: 'SonarQube',
    label: 'Test Coverage',
    definition: 'Percentage of code covered by automated unit tests, as measured by SonarQube analysis.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n >= 80
        ? `Test coverage is ${n}% — above the recommended 80% threshold.`
        : n > 0
          ? `Test coverage is only ${n}% — below the recommended 80% threshold, leaving significant portions of code untested.`
          : 'No test coverage data available or coverage is 0%.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n < 80 ? [`Increase unit test coverage from ${n}% toward the 80% target — focus on security-critical modules and core business logic.`] : [];
    },
  },

  // ── BlackDuck ──
  {
    id: 'bd_critical',
    tool: 'BlackDuck',
    label: 'Critical CVEs',
    definition: 'Critical severity CVEs (CVSS ≥ 9.0) found in open source dependencies — directly exploitable with severe impact.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} critical CVE${n === 1 ? '' : 's'} found in open source dependencies — these have CVSS ≥ 9.0 and are directly exploitable.`
        : 'No critical CVEs found in dependencies.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Upgrade ${n} dependenc${n === 1 ? 'y' : 'ies'} with critical CVEs to a patched version.`] : [];
    },
  },
  {
    id: 'bd_high',
    tool: 'BlackDuck',
    label: 'High CVEs',
    definition: 'High severity CVEs (CVSS 7.0–8.9) found in open source dependencies.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} high severity CVE${n === 1 ? '' : 's'} found in open source dependencies.`
        : 'No high severity CVEs found in dependencies.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Upgrade ${n} dependenc${n === 1 ? 'y' : 'ies'} with high CVEs to a patched version.`] : [];
    },
  },
  {
    id: 'bd_licence_high',
    tool: 'BlackDuck',
    label: 'High-Risk License Violations',
    definition: "Dependencies with restrictive or copyleft licenses (GPL, AGPL) that may conflict with the project's licensing.",
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} component${n === 1 ? '' : 's'} are distributed under a restrictive/copyleft license (e.g. GPL, AGPL), pulled in as a direct or transitive dependency.`
        : 'No high-risk license violations found.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Review ${n} high-risk license violation${n === 1 ? '' : 's'} with Legal — replace the component or obtain an exception.`] : [];
    },
    itemsUnavailableHint: (v) => numVal(v) > 0
      ? 'BlackDuck only reports the high-risk license count here — open the License Risk view in BlackDuck to see affected components.'
      : undefined,
  },
  {
    id: 'bd_medium',
    tool: 'BlackDuck',
    label: 'Medium CVEs',
    definition: 'Medium severity CVEs (CVSS 4.0–6.9) found in open source dependencies.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} medium severity CVE${n === 1 ? '' : 's'} found in open source dependencies.`
        : 'No medium severity CVEs found in dependencies.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Review and upgrade ${n} dependenc${n === 1 ? 'y' : 'ies'} with medium CVEs when feasible.`] : [];
    },
  },

  // ── Gitleaks / GHE ──
  {
    id: 'secrets_detected',
    tool: 'Gitleaks',
    label: 'Hardcoded Secrets Detected',
    definition: 'Secrets (API keys, passwords, tokens) found hardcoded in source or open GHE secret-scanning alerts.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} hardcoded secret${n === 1 ? '' : 's'} ${n === 1 ? 'is' : 'are'} flagged as open alerts — any secret in code requires immediate credential rotation.`
        : 'No hardcoded secrets detected.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Rotate and remove ${n} hardcoded secret${n === 1 ? '' : 's'}, then resolve the alert(s).`] : [];
    },
  },
  {
    id: 'branch_protected',
    tool: 'Gitleaks',
    label: 'Branch Protection Enabled',
    definition: 'Whether the scanned branch requires PR review, blocks force-push, and requires status checks before merge.',
    causeTemplate: (v) => boolVal(v)
      ? 'Branch protection is enabled for this repository.'
      : 'This branch has no protection rules — anyone can push directly, with no review or safety net.',
    remediationTemplate: (v) => boolVal(v) ? [] : ['Enable branch protection (require PR review + status checks) on the scanned branch.'],
  },
  {
    id: 'pipeline_success',
    tool: 'Gitleaks',
    label: 'Pipeline Success',
    definition: 'Whether the CI/CD pipeline for this repository is currently passing.',
    causeTemplate: (v) => boolVal(v)
      ? 'The CI/CD pipeline is passing.'
      : 'The CI/CD pipeline is currently failing — build or test steps are broken.',
    remediationTemplate: (v) => boolVal(v) ? [] : ['Fix the failing CI/CD pipeline steps (build errors, test failures).'],
  },
  {
    id: 'stale_prs',
    tool: 'Gitleaks',
    label: 'Stale PRs',
    definition: 'Open pull requests that have had no activity for an extended period, indicating potential neglect.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} open pull request${n === 1 ? '' : 's'} ha${n === 1 ? 's' : 've'} had no activity recently.`
        : 'No stale pull requests.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Review and resolve ${n} stale PR${n === 1 ? '' : 's'} — merge, close, or request updates.`] : [];
    },
  },

  // ── Trivy ──
  {
    id: 'image_scan_pass',
    tool: 'Trivy',
    label: 'Container Image Scan',
    definition: 'Whether the container image passes the configured security policy (no critical/high CVEs in image layers or base image).',
    causeTemplate: (v) => boolVal(v)
      ? 'The latest container image passed the security policy.'
      : 'The latest container image failed the security policy — critical/high CVEs were found in image layers or the base image.',
    remediationTemplate: (v) => boolVal(v) ? [] : ['Rebuild the image on a patched base image / updated packages to clear the failing CVEs.'],
  },
  {
    id: 'image_critical_cves',
    tool: 'Trivy',
    label: 'Container Critical CVEs',
    definition: 'Critical CVEs found in the container image (base OS packages, runtime, installed tools) — directly exploitable at runtime.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} critical CVE${n === 1 ? '' : 's'} found in this image's layers or base image.`
        : 'No critical CVEs found in this image.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Patch or rebuild the image to clear ${n} critical CVE${n === 1 ? '' : 's'}.`] : [];
    },
  },
  {
    id: 'image_high_cves',
    tool: 'Trivy',
    label: 'Container High CVEs',
    definition: 'High severity CVEs found in the container image.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} high severity CVE${n === 1 ? '' : 's'} found in this image.`
        : 'No high CVEs found in this image.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Patch or rebuild the image to clear ${n} high CVE${n === 1 ? '' : 's'}.`] : [];
    },
  },
  {
    id: 'image_age_days',
    tool: 'Trivy',
    label: 'Image Age',
    definition: 'How many days since the container image was last built. Older images are more likely to contain unpatched vulnerabilities.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 90
        ? `This image is ${n} days old — images older than 90 days should be rebuilt with updated packages.`
        : `This image is ${n} day${n === 1 ? '' : 's'} old.`;
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 90 ? [`Rebuild the container image (currently ${n} days old) with up-to-date base and packages.`] : [];
    },
  },

  // ── Delta AI KPIs ──
  {
    id: 'epss_score',
    tool: 'Delta AI',
    label: 'EPSS Score',
    definition: 'Exploit Prediction Scoring System — probability (0–100) that a vulnerability will be exploited in the wild within 30 days.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 50
        ? `EPSS score is ${n}, indicating a high probability of exploitation in the next 30 days.`
        : `EPSS score is ${n}, indicating a moderate-to-low exploitation probability.`;
    },
    remediationTemplate: (v) => numVal(v) > 50
      ? ['Prioritize patching vulnerabilities with high EPSS scores — these are most likely to be exploited.']
      : [],
  },
  {
    id: 'cisa_kev_match_count',
    tool: 'Delta AI',
    label: 'CISA KEV Matches',
    definition: 'Number of vulnerabilities matching CISA\'s Known Exploited Vulnerabilities catalog — confirmed actively exploited in the wild.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} vulnerabilit${n === 1 ? 'y matches' : 'ies match'} CISA's Known Exploited Vulnerabilities catalog — these are confirmed actively exploited.`
        : 'No CISA KEV matches found.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Immediately patch ${n} CISA KEV-listed vulnerabilit${n === 1 ? 'y' : 'ies'} — these are actively exploited in the wild.`] : [];
    },
  },
  {
    id: 'reachable_vulnerable_functions',
    tool: 'Delta AI',
    label: 'Reachable Vulnerable Functions',
    definition: 'Vulnerable functions in dependencies that are actually called/reachable from application code — higher risk than unreachable ones.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} vulnerable function${n === 1 ? '' : 's'} in dependencies ${n === 1 ? 'is' : 'are'} reachable from application code.`
        : 'No reachable vulnerable functions detected.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Upgrade dependencies to eliminate ${n} reachable vulnerable function${n === 1 ? '' : 's'}, or add mitigating controls.`] : [];
    },
  },
  {
    id: 'unreachable_cve_pct',
    tool: 'Delta AI',
    label: 'Unreachable CVE %',
    definition: 'Percentage of CVEs in dependencies that are NOT reachable from application code — these can be deprioritized.',
    causeTemplate: (v) => `${numVal(v)}% of CVEs in dependencies are not reachable from application code and can be deprioritized.`,
    remediationTemplate: () => [],
  },
  {
    id: 'mttr_critical',
    tool: 'Delta AI',
    label: 'MTTR Critical',
    definition: 'Mean Time To Remediate for critical vulnerabilities — average days from discovery to fix.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 30
        ? `Average time to fix critical vulnerabilities is ${n} days — above the 30-day target.`
        : `Average time to fix critical vulnerabilities is ${n} day${n === 1 ? '' : 's'}.`;
    },
    remediationTemplate: (v) => numVal(v) > 30
      ? ['Reduce MTTR for critical vulnerabilities by establishing faster triage and patching workflows.']
      : [],
  },
  {
    id: 'security_regression_rate',
    tool: 'Delta AI',
    label: 'Security Regression Rate',
    definition: 'Percentage of previously fixed vulnerabilities that have reappeared — indicates process/dependency management issues.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n}% of previously fixed vulnerabilities have reappeared.`
        : 'No security regressions detected.';
    },
    remediationTemplate: (v) => numVal(v) > 0
      ? ['Pin dependency versions and add automated checks to prevent previously fixed vulnerabilities from reappearing.']
      : [],
  },
  {
    id: 'sla_breach_count',
    tool: 'Delta AI',
    label: 'SLA Breaches',
    definition: 'Number of vulnerabilities that have exceeded their remediation SLA deadline.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 0
        ? `${n} vulnerabilit${n === 1 ? 'y has' : 'ies have'} exceeded their remediation SLA deadline.`
        : 'No SLA breaches — all vulnerabilities are being addressed within their deadlines.';
    },
    remediationTemplate: (v) => {
      const n = numVal(v);
      return n > 0 ? [`Address ${n} SLA-breaching vulnerabilit${n === 1 ? 'y' : 'ies'} immediately.`] : [];
    },
  },
  {
    id: 'sbom_coverage_pct',
    tool: 'Delta AI',
    label: 'SBOM Coverage',
    definition: 'Percentage of dependencies that are covered by a Software Bill of Materials (SBOM).',
    causeTemplate: (v) => `SBOM covers ${numVal(v)}% of dependencies.`,
    remediationTemplate: (v) => numVal(v) < 90
      ? ['Improve SBOM coverage by ensuring all build tools and package managers contribute to the SBOM generation.']
      : [],
  },
  {
    id: 'transitive_dependency_depth',
    tool: 'Delta AI',
    label: 'Dependency Depth',
    definition: 'Maximum depth of the transitive dependency tree — deeper trees are harder to audit and more likely to contain hidden vulnerabilities.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 5
        ? `Dependency tree is ${n} levels deep — consider flattening by replacing deeply nested packages.`
        : `Dependency tree depth is ${n} — within acceptable limits.`;
    },
    remediationTemplate: (v) => numVal(v) > 5
      ? ['Audit and reduce transitive dependency depth by replacing deeply nested or unmaintained packages.']
      : [],
  },
  {
    id: 'dependency_confusion_risk_score',
    tool: 'Delta AI',
    label: 'Dependency Confusion Risk',
    definition: 'Risk score for dependency confusion attacks — where internal package names could be hijacked by malicious public packages.',
    causeTemplate: (v) => {
      const n = numVal(v);
      return n > 50
        ? `Dependency confusion risk score is ${n} — internal packages may be vulnerable to namespace hijacking.`
        : `Dependency confusion risk score is ${n} — within acceptable limits.`;
    },
    remediationTemplate: (v) => numVal(v) > 50
      ? ['Configure scoped registries and namespace reservations to prevent dependency confusion attacks.']
      : [],
  },
];

// ── Lookup Map ──────────────────────────────────────────────────
const SPEC_MAP = new Map<string, KpiSpec>(KPI_SPECS.map((s) => [s.id, s]));

/**
 * Returns a fully resolved KPI definition with cause and remediation text
 * evaluated against the provided value.
 */
export function getKpiDefinition(kpiId: string, value?: unknown): KpiDefinition | null {
  const spec = SPEC_MAP.get(kpiId);
  if (!spec) return null;
  return {
    id: spec.id,
    tool: spec.tool,
    label: spec.label,
    definition: spec.definition,
    cause: spec.causeTemplate(value),
    remediation: spec.remediationTemplate(value),
    itemsUnavailableHint: spec.itemsUnavailableHint?.(value),
  };
}

/**
 * Returns the static label for a KPI id, or the id itself if not found.
 */
export function getKpiLabel(kpiId: string): string {
  return SPEC_MAP.get(kpiId)?.label || kpiId;
}

/**
 * Returns the tool name for a KPI id.
 */
export function getKpiTool(kpiId: string): string {
  return SPEC_MAP.get(kpiId)?.tool || 'Unknown';
}
